// AI 利用量の計量軸（Issue #475 / #477）の語彙と表示ラベル。
//
// 「利用量そのものの取得・表示（features/ai-usage）」と「上限超過エラーの表示
// （core/utils/http-error.utils.ts）」の両方が同じ軸を扱うため、**軸の定義はここを
// 単一ソース**にする（同じ語彙を 2 か所で定義するとラベルがドリフトする）。
// OCR 失敗理由（core/utils/ocr-failed-reason.utils.ts）と同じ置き方。

/**
 * AI 利用量の軸（openapi `AiUsageItem.type` / `@domain` の `AI_USAGE_TYPES` と同値）。
 * viewer は `@domain` を import できないため同値の union をローカル定義する。
 */
export const AI_USAGE_TYPE_VALUES = [
  'execution_cost_usd',
  'extraction_cost_usd',
] as const;

export type AiUsageType = (typeof AI_USAGE_TYPE_VALUES)[number];

/**
 * 軸ごとの利用量残高（openapi `AiUsageItem`）。
 *
 * - `type` は将来 AI サーバー側で軸が増えても壊れないよう `string` で受ける
 *   （既知の軸は `AiUsageType`）
 * - `remaining` は **事後型 enforcement のため負値がありうる**。0 クランプや
 *   `granted - consumed` の再計算はしない（AI サーバーの計量値を無加工で運ぶ）
 */
export type AiUsageItem = {
  type: string;
  unit: string;
  granted: number;
  consumed: number;
  remaining: number;
};

/** 軸のユーザー向け表示名（`execution_cost_usd` = AI 審査の実行 / `extraction_cost_usd` = OCR） */
export const AI_USAGE_TYPE_LABELS: Record<AiUsageType, string> = {
  execution_cost_usd: 'AI 実行',
  extraction_cost_usd: 'OCR',
};

/** 既知の計量軸かどうかの型ガード */
export const isAiUsageType = (value: unknown): value is AiUsageType =>
  typeof value === 'string' &&
  (AI_USAGE_TYPE_VALUES as readonly string[]).includes(value);

/**
 * 計量軸の表示名を返す。
 *
 * 未知の軸（AI サーバー側で軸が増えた場合）は生の値をそのまま返す。
 * 意味の分からない軸に日本語ラベルを当てて誤認させないための方針。
 */
export const aiUsageTypeLabel = (type: string): string =>
  isAiUsageType(type) ? AI_USAGE_TYPE_LABELS[type] : type;
